import { useChat } from "@ai-sdk/react";
import { createFileRoute } from "@tanstack/react-router";
import { DefaultChatTransport } from "ai";
import type React from "react";
import { useMemo } from "react";

import { ChatBubble, ChatComposer, ChatMessageList } from "~/components/chat";
import { MarkdownContent } from "~/components/markdown-content";
import { Button } from "~/components/ui/button";
import { requirePermission } from "~/lib/functions/admin-access";

export const Route = createFileRoute("/admin/mpai/")({
  staticData: { title: "MPAI Test Chat" },
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "mpai:read"),
  component: MpaiTestChatPage,
});

type UsageMetadata = {
  usage?: { inputTokens?: number; outputTokens?: number };
};

function MpaiTestChatPage(): React.ReactElement {
  const transport = useMemo(
    () => new DefaultChatTransport({ api: "/api/admin/mpai-chat" }),
    [],
  );
  const { messages, sendMessage, setMessages, status, stop, error } = useChat({
    transport,
  });
  const isBusy = status === "submitted" || status === "streaming";

  return (
    <div className="flex h-[calc(100dvh-var(--app-topbar-h)-3rem)] flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-medium">MPAI Test Chat</h1>
          <p className="text-text-secondary text-sm">
            Talks straight to Multi-Perspective AI. Nothing is saved.
          </p>
        </div>
        <Button
          onClick={() => setMessages([])}
          disabled={isBusy || messages.length === 0}
        >
          New conversation
        </Button>
      </div>

      <ChatMessageList className="flex-1 rounded-lg border border-border p-4">
        {messages.map((message) => {
          const text = message.parts
            .map((part) => (part.type === "text" ? part.text : ""))
            .join("");
          const usage = (message.metadata as UsageMetadata | undefined)?.usage;
          return (
            <ChatBubble
              key={message.id}
              author={message.role === "user" ? "me" : "them"}
              timestamp={
                usage
                  ? `${usage.inputTokens ?? "?"} in · ${usage.outputTokens ?? "?"} out tokens`
                  : undefined
              }
            >
              {message.role === "user" ? (
                <p className="whitespace-pre-wrap">{text}</p>
              ) : (
                <MarkdownContent content={text} />
              )}
            </ChatBubble>
          );
        })}
      </ChatMessageList>

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error.message}
        </p>
      )}

      <ChatComposer
        placeholder="Message MPAI…"
        submitOn="mod-enter"
        hint="⌘↵ to send"
        isPending={isBusy}
        lockWhilePending
        status={status === "streaming" ? "streaming" : "idle"}
        onCancel={stop}
        onSubmit={(text) => sendMessage({ text })}
        autoFocus
      />
    </div>
  );
}
